/* --------------- UTILITIES : CSS --------------- */
// shared across all components



// .......... FONT BASELINE .......... //
// baseline font size that all other element sizes will be based off of
// used in index_styles for body font-size and used for pxToEm conversion
const fontSizeBaseline = 16;



// .......... UNIT CONVERSIONS .......... //
// converts pixels in styles to EMs 
// this allows us to create styles as pixels to better match given designs, then convert those to relative units so we can achieve responsive design
let pxToEm = (val) => parseFloat(val) / fontSizeBaseline + "em";
// create some values as rems so they aren't as affected by font size
let pxToRem = (val) => parseFloat(val) / fontSizeBaseline + "rem";
// convert em to pixel (helpful with perctage conversions)
let emToPx = (val) => parseFloat(val) * fontSizeBaseline + "px";
// convert percentages to pixels then to em value (needed for dynamic content sizing based off of content space DOM insertions)
let percToEm = (parSize, elSize) => pxToEm(parseFloat(emToPx(parSize))*(parseFloat(elSize)/100)); //log(parSize, elSize);
// convert style string px to em (used by store.js to convert settings file layouts and content styles from pixels to ems)
let replacePXinStringWithEM = (passedStyle) => {
	// save version of passed style
	let style = passedStyle;
	// if style contains pixels
	if (style.match(/(\d*(\.\d*)?)px/g)) {
		// store individual px in array
		let pxVals = style.match(/(\d*(\.\d*)?)px/g);
		// log(style);
		// log(pxVals);
		// convert to ems
		let emVals = pxVals.map(px => pxToEm(px));
		// log(emVals);
		// for every pixel we found, replace the px in the original style with the converted em (by matching the index of that array)
		pxVals.forEach((val, i) => {
			style = style.replace(val, emVals[i]);
		})
		// log(style);
	}
	return style;
};
// styles (as object) are passed in here and if any of its rules have px values they will be converted to em
let convertStylesPXtoEM = (passedStyles) => {
	// save version of passed styles
	let styles = Object.assign({}, passedStyles);
	// get each style
    Object.entries(styles).forEach((style) => {
        // check if style contains child styles (probably need to make this smarter as this only goes one level deep)
        if (typeof style[1] === "object") {
            Object.entries(style[1]).forEach((nestedStyle) => {
            	// log(nestedStyle[0]);
            	styles[style[0]][nestedStyle[0]] = replacePXinStringWithEM(nestedStyle[1]);
            });
        } else {
            styles[style[0]] = replacePXinStringWithEM(style[1]);
        }
    });
    // log(styles);
    return styles;
};




// .......... CONVERT HEX TO RGB .......... //
// convert hex to rgb (with opacity param adjuster)
const convertHexToRGB = (hex,opacity) => {
    hex = hex.replace('#','');
    let r = parseInt(hex.substring(0,2), 16);
    let g = parseInt(hex.substring(2,4), 16);
    let b = parseInt(hex.substring(4,6), 16);
    let result = 'rgba('+r+','+g+','+b+','+opacity/100+')';
    return result;
};



// .......... CENTERING .......... //
// center element absolutely
const centerAbsolutely = {
	position: 'absolute',
	top: '50%',
	left: '50%',
	transform: 'translate(-50%, -50%)'
};
const centerVertically = {
	position: 'absolute',
	top: '50%',
	transform: 'translateY(-50%)'
};
const centerHorizontally = {
	position: 'absolute',
	left: '50%',
	transform: 'translateX(-50%)'
};
const removeCenterAbsolutely = {
	position: 'relative',
	top: '0',
	left: '0',
	transform: 'none'
};



// ......... COLORED SCROLL BAR ...........//
const coloredScrollBar= (color) =>( {
    '&::-webkit-scrollbar':{
        width: pxToEm(5),
		height: pxToEm(5)
    },
    '&::-webkit-scrollbar-thumb': {
        background: color
    },
});



// .......... FULL SIZE ELEMENT .......... //
// full width and height
const fullSize = {
	width: '100%',
	height: '100%'
};



// .......... ELLIPSIS .......... //
const ellipsis = {
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis'
};



// .......... DISABLE .......... //
const disable = {
	pointerEvents: 'none',
	filter: 'grayscale(1)',
	opacity: '.4'
};



// .......... GET CSS PROPERTY .......... //
const getElementCSSProperty = (element, property) => window.getComputedStyle(element, null).getPropertyValue(property);




// .......... CLASS STYLE HELPERS .......... //
const classStyleHelper = {
	align: {
		'&.align-center': {
			textAlign: 'center',
			justifyContent: 'center',
			alignContent: 'center',
			alignItems: 'center'
		},
		'&.align-left': {
			textAlign: 'left',
			justifyContent: 'flex-start',
			alignContent: 'flex-start',
			alignItems: 'flex-start'
		},
		'&.align-right': {
			textAlign: 'right',
			justifyContent: 'flex-end',
			alignContent: 'flex-end',
			alignItems: 'flex-end'
		}
	},
	direction: {
		'&.direction-vertical': {
			display: 'flex',
			flexDirection: 'column'
		},
		'&.direction-horizontal': {
			display: 'flex',
			flexDirection: 'row'
		}
	}
};



// .......... EMBED OVERRIDES (for content cards) .......... //
const embedOverrides = (percentage) => {
	return {
		'& .embed': {
			height: '100%',
			fontSize: `${percentage}%`,
			'& .structureinner': { fontSize: '80%' },
			'& .question': { fontSize: '80%' }
		}
	}
};




// .......... CSS IMAGES .......... //
// background image
const cssBGImage = {
	backgroundColor: 'transparent',
	backgroundPosition: '50%',
	backgroundSize: 'contain',
	backgroundRepeat: 'no-repeat'
	// backgroundImage will be placed per unique style
};
// general image settings
const cssImage = {
	backgroundColor: 'transparent',
	backgroundSize: 'contain',
	backgroundRepeat: 'no-repeat',
	backgroundPosition: '50%',
	display: 'inline-block',
	verticalAlign: 'middle',
	'&:hover': {
		transition: 'all .2s linear 0s'
	}
};
// image types
const imageTypes = {
	icon: {
		extend: cssImage
	},
	logo: {
		extend: cssImage
	},
	graphic: {
		extend: cssBGImage
	},
	photo: {
		extend: cssBGImage
	}
};





// .......... BOX SHADOW .......... //
// shadow styles
const boxShadow = {
	blue: { boxShadow: replacePXinStringWithEM(`0 0 20px 0 ${convertHexToRGB('#1494F4', 90)}`) },
	blueLarge: { boxShadow: replacePXinStringWithEM(`0 0 40px 0 ${convertHexToRGB('#1494F4', 90)}`) },
	grey: { boxShadow: replacePXinStringWithEM(`0 0 10px 0 rgba(0,0,0,0.15)`) },
	greyLarge: { boxShadow: replacePXinStringWithEM(`0 0 20px 0 rgba(0,0,0,0.15)`) },
	greySmall: { boxShadow: replacePXinStringWithEM(`0 0 2px 0 rgba(0,0,0,0.02)`) }
};




// .......... FIELD HELPER .......... //
const field = {
	outline: 'none',
	backgroundColor: '#fff', 
	color: '#ccc',
	border: replacePXinStringWithEM(`1px solid '#ccc`),
	transition: 'all .2s linear 0s',
	'&:hover': {
		extend: boxShadow.grey
	},
	'&:focus': {
		borderColor: '#1494F4',
		extend: boxShadow.blue
	}
};





// .......... ANIMATIONS .......... //
const animations = {
	clicked: {
		animationName: 'rubberBand',
	    animationDelay: '0s',
	    animationDuration: '.5s',
	},
	imageHover: {
		animationName: 'pulse',
        animationDelay: '0s',
        animationDuration: '.5s'
	},
	invalid: {
		animationName: 'headShake',
        animationDelay: '0s',
        animationDuration: '.5s'
	}
};





// .......... OVERLAY STYLES .......... //
const overlayStyles = {
	borderRadius: 'inherit',
	border: `4px solid #1494F4`,
	background: `rgba(20, 148, 244, 0.6)`,
	display: 'block',
	width: '100%',
	height: '100%',
	position: 'absolute',
	top: '0',
	left: '0',
};



// .......... ANSWER SELECT .......... //
const answerSelect = {
	'& .overlay': {
		opacity: '0', // animated by result state
		extend: overlayStyles,
		zIndex: '-1',
		transition: 'opacity .2s linear 0s',
		'& .icon': {
			display: 'none',
			zIndex: '-1'
		}
	},
	'&.selected-true': {
		'& .overlay': {
			opacity: '1',
			zIndex: '8',
			'& .icon': {
				color: '#fff',
				display: 'block',
				fontSize: '215%',
				extend: centerAbsolutely,
				zIndex: '3'
			}
		},
		'& .imageindicator': {
			opacity: '0'
		}
	}
};




// .......... ANSWER ERROR .......... //
const answerError = {
	text: {
		background: convertHexToRGB('#FFD1D1', 40),
		border: replacePXinStringWithEM(`2px solid #FFD1D1`)
	},
	image: {
		'& .overlay': {
			background: convertHexToRGB('#FFD1D1', 40),
			border: replacePXinStringWithEM(`2px solid #FFD1D1`),
			opacity: '1',
			zIndex: '8',
			boxShadow: 'none'
		}
	}
};




// .......... OVERLAY (images and content cards) .......... //
const overlay = {
	'&:after': {
		extend: overlayStyles,
		opacity: '0',
		content: '" "',
		zIndex: '2',
	},
	'&:hover': {
		'&:after': {
			animationName: 'fadeIn',
			animationDelay: '0s',
			animationDuration: '.2s',
			animationFillMode: 'forwards',	
		}
	}
};




// .......... THEME HELPER .......... //
// loop each property in a theme and create styles with property infused
// example pass 'color' as themeProp, this will print '&.color-blue' in style sheet, then pass styles you want for that in "cb" function
const themeStyleHelper = (theme, themeProp, cb) => {
	let styleObj = {};
	Object.entries(theme[themeProp]).forEach((prop) => {
		styleObj = Object.assign({}, styleObj, { [`&.${themeProp}-${prop[0]}`]: cb(theme, prop[0]) });
	});
	return styleObj;
};





// .......... NTH CHILD ITERATOR .......... //
const nthChildIterator = (numOfChildren, initialNum, numVariation) => {
	let styleObj = {};
	let num = initialNum;
	for (let x = 0; x < numOfChildren; x++) {
		styleObj = Object.assign({}, styleObj, { 
			[`&:nth-child(${x + 1})`]: { animationDelay: `${num}s` }
		});
		num += numVariation;
	}
	return styleObj;
};
// extend: nthChildIterator(3, 0, .2)
// the above will print this:
// '&:nth-child(1)': { animationDelay: '.2s' },
// '&:nth-child(2)': { animationDelay: '.4s' },
// '&:nth-child(3)': { animationDelay: '.6s' }






// .......... MEDIA QUERIES .......... //
// media queries
// http://giona.net/tools/css3-mediaquery-generator/
const mediaQuery = {
	customizeRailThin: `@media only screen and (min-width:${pxToEm(800)}) and (max-width:${pxToEm(1090)})`,
	customizeInContentThin: `@media only screen and (min-width:${pxToEm(800)}) and (max-width:${pxToEm(1400)})`,
	customizeThinnest: `@media only screen and (min-width:${pxToEm(0)}) and (max-width:${pxToEm(799)})`,
	homepageDateDropdown: `@media only screen and (min-width:${pxToEm(700)}) and (max-width:${pxToEm(1000)})`,
	reportDetailPage: `@media only screen and (min-width:${pxToEm(0)}) and (max-width:${pxToEm(1260)})`,
	medium: `@media only screen and (min-width:${pxToEm(0)}) and (max-width:${pxToEm(1200)})`,
	thinnest: `@media only screen and (min-width:${pxToEm(0)}) and (max-width:${pxToEm(700)})`,
};
const embedMediaQuery = {
	wide: `@media only screen and (min-width:${pxToEm(560)})`,
	medium: `@media only screen and (min-width:${pxToEm(400)}) and (max-width:${pxToEm(560)})`,
	thin: `@media only screen and (min-width:${pxToEm(0)}) and (max-width:${pxToEm(270)})`,
	tall: `@media only screen and (max-height:${pxToEm(450)})`,
	short: `@media only screen and (max-height:${pxToEm(250)})`
};



/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export { fontSizeBaseline, pxToEm, pxToRem, percToEm, convertHexToRGB, ellipsis, mediaQuery, embedMediaQuery, fullSize, centerAbsolutely, centerVertically, centerHorizontally, coloredScrollBar, removeCenterAbsolutely, replacePXinStringWithEM, convertStylesPXtoEM, getElementCSSProperty, themeStyleHelper, classStyleHelper, disable, embedOverrides, imageTypes, field, boxShadow, animations, answerError, answerSelect, overlay, overlayStyles, nthChildIterator };